import {Injectable} from '@angular/core';
import {FormGroup, FormArray, FormControl, ValidatorFn} from '@angular/forms';
import {ValidationContext} from './validation-context';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {FieldTypes} from "../model/field.model";
import {HttpClient, HttpParams} from "@angular/common/http";

@Injectable()
export class GenericFormService {

	formObserver = new BehaviorSubject(undefined);
	submitObserver = new Subject();
	onChangeOptions$ = new Subject();
	onSelectOptions$ = new Subject();
	onKeyFilterOptions$ = new Subject();
	onPickListChange$ = new Subject();
	onSwitchChange$ = new Subject();
	onChangeText$ = new Subject();

	form: FormGroup;
	dataModel: any;
	formFieldList: any;

	constructor(
		private httpClient:HttpClient
	) {
	}

	/**
	 * initial point to create form builder
	 * ToDo:
	 */
	async initForm(dataModel: any): Promise<void> {
		const raw = "";
		this.form = undefined;
		this.dataModel = dataModel;
		this.form = this.toForm(dataModel, []) as FormGroup;
		this.markAsTouched(this.form);
		this.validate(this.form);
		this.formObserver.next(this.form);
	}

	setFormFieldList(formFieldList: any) {
		this.formFieldList = formFieldList;
	}

	/**
	 * adds missing properties to the form builder
	 * @param c
	 * @param path
	 */
	addMissingProperties(dataModel: any): void {
		for (const key in dataModel) {
			const control = this.form.get(key) as FormGroup;
			if (control.controls) {
				this.form.setControl(key, new FormControl('', []));
			}
		}
	}

	/**
	 * Recursive function
	 * transforms json object into FormBuilder
	 * @param obj
	 * @param path
	 */
	toForm(obj: any, path: any[]): any {
		const metadata = {};
		const result: any = obj.constructor.name !== 'Array' ?
			new FormGroup({}, metadata ? this.requirednessValidator(metadata) : null) :
			new FormArray([], metadata ? this.requirednessValidator(metadata) : null);

		for (const key in obj) {
			if (obj[key] !== null && typeof obj[key] === 'object'
				&& (
					this.formFieldList[key]?.UIControl !== FieldTypes.AUTOCOMPLETE &&
					this.formFieldList[key]?.UIControl !== FieldTypes.MULTISELECT &&
					this.formFieldList[key]?.UIControl !== FieldTypes.PICKLIST &&
					!this.formFieldList[key]?.UIOptions?.isArray

				)) {
				result.setControl(key, this.toForm(obj[key], [...path, `${key}`]));
			} else {
				result.setControl(
					key,
					new FormControl(
						obj[key],
						this.requirednessValidator(
							{}
						)
					)
				);
			}
		}
		return result;
	}

	/**
	 * Recursive function
	 * transforms schema into FormBuilder base
	 * @param obj
	 */
	public toData(obj: any): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			if (key === 'Metadata') {
				continue;
			}

			let newKey = key;
			if (['InspectionReport', 'CV_EXTENSION_SECTION'].indexOf(key) === -1) {
				newKey = `${key.charAt(0).toLocaleLowerCase()}${key.slice(1)}`;
			}

			result[newKey] = this.toData(obj[key]);
		}
		return result;
	}

	/**
	 * Return the validation function from the schema given
	 *
	 * @param schema
	 */
	requirednessValidator(schema: any): ValidatorFn {
		return control => {
			if (this.form === undefined || schema === undefined || control.root instanceof FormControl) {
				return null;
			}

			let message = schema.Name ? `${schema.Name} is required` : '';
			let isInvalid = false;

			if (schema.Required !== undefined && schema.Required === 'True') {
				isInvalid = control instanceof FormArray ?
					(control as FormArray).controls.length === 0 :
					(control.value === null || control.value.toString().trim() === '');
			} else if (schema.Conditionally !== undefined) {
				// tslint:disable-next-line:no-eval
				const evaluation: any = eval(`(${schema.Conditionally})`)({}, new ValidationContext(control));
				if (typeof evaluation === 'object') {
					message = evaluation._label;
				}
				isInvalid = evaluation as boolean;
			}

			return isInvalid ? {required: message} : null;
		};
	}

	/**
	 * Recursive function
	 * Skip objects from the FromBuilder values
	 * @param obj
	 * @param callback
	 */
	public omit(obj: any, callback: (key: string | number, value: any) => boolean): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			if (callback(key, obj[key]) && key !== 'cv_photos') {
				if (obj[key] && typeof obj[key] === 'object') {
					result[key] = this.omit(obj[key], callback);
				} else {
					result[key] = obj[key];
				}
			}
		}
		return result;
	}

	/**
	 * Recursive function
	 * Fires the validation of the FormBuilder
	 * @param c
	 */
	validate(c: any): void {
		// tslint:disable-next-line:forin
		for (const key in c.controls) {
			const control = c.get(key);
			control.updateValueAndValidity({onlySelf: true});
			if (control instanceof FormGroup ||
				control instanceof FormArray) {
				this.validate(control);
			}
		}
		c.updateValueAndValidity();
	}

	/**
	 * Recursive Function
	 * Marks the control as touched, to fire the validation UI
	 * @param formGroup
	 */
	markAsTouched(formGroup: FormGroup): void {
		(Object as any).values(formGroup.controls).forEach((control: FormGroup) => {
			control.markAsTouched();
			if (control.controls) {
				this.markAsTouched(control);
			}
		});
	}

	/**
	 * Patch the full form values
	 *
	 * @param newValues JSON document
	 */
	patchFormValues(newValues?: any): void {
		let values = newValues;
		if (!values) {
			values = {};
		}

		this.form.patchValue(values);
	}

	createChilds(key: string, childs: any): void {
		const metadata = {};
		const formArray =  this.form.get(key) as FormArray;
		if(formArray){
			formArray.push(this.toForm(childs, [...[], `${key}`]));
		}
	}

	/**
	 * Event to get options in external endpoints for dropdowns
	 * @param url
	 * @param filterName
	 * @param name
	 */
	public getDataByUrl(url: string, filterName: string,  name: string): Observable<any> {
		let httpParams: any = new HttpParams();
		if (name && name !== '') {
			httpParams = httpParams.append(filterName, name);
		}

		return Observable.create((observer: any) => {
			this.httpClient.get(url, { params: httpParams })
				.subscribe(
					(result: any) => {
						const response = result.data ? result.data : result;
						observer.next(response);
						observer.complete();
					},
					error => console.error(error)
				);
		});
	}

}
